import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Shared table-toolbar dropdown filters.
 * Values live on custom URL keys (not filter_*) so they stay independent of FilterBuilder.
 */

export function asOptionList(payload, keys = []) {
  if (Array.isArray(payload)) return payload;
  const tryKeys = [
    ...keys,
    "data",
    "Departments",
    "Levels",
    "organizationdesignation",
    "workshifts",
    "increments",
    "intership",
    "functional",
    "leavetypes",
    "exitreason",
    "exitreasontype",
    "workshifttypes",
    "workshifttype",
    "documenttypes",
    "documentType",
    "Documents",
    "recruitment_source_types",
    "recruitment_sources",
    "organizationbusinessregistrationtype",
    "userTypes",
    "userroles",
    "skill",
    "learning",
  ];
  for (const key of tryKeys) {
    const val = payload?.[key];
    if (Array.isArray(val)) return val;
    if (Array.isArray(val?.data)) return val.data;
  }
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

export function useToolbarFilterChange(setSearchParams) {
  return useCallback(
    (key, value, extraClears = []) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        extraClears.forEach((clearKey) => next.delete(clearKey));
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );
}

export function mergeToolbarFilters(filters, searchParams, mappings = []) {
  const fields = mappings.map((m) => m.field);
  const next = (Array.isArray(filters) ? filters : []).filter(
    (f) => !fields.includes(f?.field)
  );

  mappings.forEach(({ urlKey, field, operator = "equals" }) => {
    const raw = searchParams.get(urlKey);
    if (!raw) return;
    next.push({
      field,
      operator,
      value: [raw],
    });
  });

  return next;
}

const MONTH_NAME_TO_NUMBER = {
  january: "1",
  february: "2",
  march: "3",
  april: "4",
  may: "5",
  june: "6",
  july: "7",
  august: "8",
  september: "9",
  october: "10",
  november: "11",
  december: "12",
};

function collectToolbarRowValues(row, field) {
  const values = [];
  const keys = new Set([field]);
  if (field === "month") {
    keys.add("month_number");
    keys.add("month_name");
  }
  if (field && !field.endsWith("_id")) {
    keys.add(`${field}_id`);
    keys.add(`organization_${field}_id`);
  }
  keys.forEach((key) => {
    const value = row?.[key];
    if (value != null && value !== "") values.push(value);
  });
  return values;
}

function normalizeToolbarCompareValue(value) {
  if (value == null) return "";
  const text = String(value).trim();
  const monthNumber = MONTH_NAME_TO_NUMBER[text.toLowerCase()];
  if (monthNumber) return monthNumber;
  const dateMatch = text.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dateMatch) return dateMatch[1];
  return text.toLowerCase();
}

function rowMatchesToolbarFilter(row, field, operator, raw) {
  const expected = normalizeToolbarCompareValue(raw);
  if (!expected) return true;
  const candidates = collectToolbarRowValues(row, field).map(normalizeToolbarCompareValue);
  if (operator === "date_equals" || operator === "on") {
    return candidates.some((value) => value === expected || value.startsWith(expected));
  }
  return candidates.includes(expected);
}

/**
 * Apply toolbar URL filters to an already-loaded row set.
 * Used only when a list falls back to an unfiltered GET (404/503).
 */
export function applyClientToolbarFilters(rows, searchParams, mappings = []) {
  if (!Array.isArray(rows) || rows.length === 0 || !mappings.length) return rows;
  return rows.filter((row) =>
    mappings.every(({ urlKey, field, operator = "equals" }) => {
      const raw = searchParams.get(urlKey);
      if (!raw) return true;
      return rowMatchesToolbarFilter(row, field, operator, raw);
    })
  );
}

function normalizeToolbarOptions(options, valueKey, labelKey) {
  if (!Array.isArray(options)) return [];
  return options
    .map((opt) => {
      if (opt == null) return null;
      const id =
        typeof opt === "object" ? opt[valueKey] ?? opt.value ?? opt.id : opt;
      const text =
        typeof opt === "object"
          ? opt[labelKey] ?? opt.label ?? String(id)
          : String(opt);
      if (id == null || id === "") return null;
      return { id: String(id), text: String(text) };
    })
    .filter(Boolean);
}

function isCompactToolbarViewport() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(max-width: 768px)").matches;
}

export function ToolbarFilterSelect({
  label,
  urlKey,
  options = [],
  valueKey,
  labelKey,
  allLabel,
  searchParams,
  onChange,
  extraClears = [],
  disabled = false,
  wide = false,
}) {
  const value = searchParams.get(urlKey) || "";
  const fallbackLabel = allLabel || `All ${label}`;
  const items = useMemo(
    () => normalizeToolbarOptions(options, valueKey, labelKey),
    [options, valueKey, labelKey]
  );
  const selectedLabel =
    items.find((item) => item.id === value)?.text || fallbackLabel;

  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const triggerRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger || isCompactToolbarViewport()) {
      setMenuStyle(null);
      return;
    }
    const rect = trigger.getBoundingClientRect();
    const pad = 12;
    const maxWidth = Math.min(320, window.innerWidth - pad * 2);
    const width = Math.min(maxWidth, Math.max(rect.width, 180));
    let left = rect.left;
    if (left + width > window.innerWidth - pad) {
      left = Math.max(pad, window.innerWidth - pad - width);
    }
    if (left < pad) left = pad;

    const spaceBelow = window.innerHeight - rect.bottom - pad;
    const spaceAbove = rect.top - pad;
    const maxHeight = Math.min(320, Math.max(spaceBelow, spaceAbove, 160));
    const openAbove =
      spaceBelow < Math.min(220, maxHeight) && spaceAbove > spaceBelow;
    const top = openAbove
      ? Math.max(pad, rect.top - maxHeight - 6)
      : rect.bottom + 6;

    setMenuStyle({
      position: "fixed",
      top,
      left,
      width,
      maxHeight,
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    const onKey = (event) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    if (isCompactToolbarViewport()) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close, updateMenuPosition]);

  const handleSelect = (nextValue) => {
    onChange(urlKey, nextValue, extraClears);
    close();
  };

  return (
    <div
      className={`ems-toolbar-select-wrap${
        wide ? " ems-toolbar-select-wrap--wide" : ""
      }`}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`ems-toolbar-select${open ? " is-open" : ""}`}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => {
          if (open) {
            close();
            return;
          }
          updateMenuPosition();
          setOpen(true);
        }}
      >
        <span className="ems-toolbar-select-value">{selectedLabel}</span>
      </button>
      {open &&
        createPortal(
          <>
            <div
              className="ems-toolbar-select-backdrop"
              onClick={close}
              aria-hidden="true"
            />
            <div
              className="ems-toolbar-select-menu"
              role="listbox"
              aria-label={label}
              style={menuStyle || undefined}
            >
              <div className="ems-toolbar-select-sheet-handle" aria-hidden="true" />
              <div className="ems-toolbar-select-menu-head">{label}</div>
              <div className="ems-toolbar-select-list">
                <button
                  type="button"
                  role="option"
                  aria-selected={!value}
                  className={`ems-toolbar-select-option${!value ? " is-selected" : ""}`}
                  onClick={() => handleSelect("")}
                >
                  {fallbackLabel}
                </button>
                {items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="option"
                    aria-selected={item.id === value}
                    className={`ems-toolbar-select-option${
                      item.id === value ? " is-selected" : ""
                    }`}
                    onClick={() => handleSelect(item.id)}
                  >
                    {item.text}
                  </button>
                ))}
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}

export function ToolbarDateFilter({
  label,
  urlKey,
  searchParams,
  onChange,
  extraClears = [],
}) {
  const value = searchParams.get(urlKey) || "";
  return (
    <input
      type="date"
      className="ems-toolbar-select"
      aria-label={label}
      value={value}
      onChange={(event) => onChange(urlKey, event.target.value, extraClears)}
    />
  );
}

export function ToolbarNumberFilter({
  label,
  urlKey,
  searchParams,
  onChange,
  extraClears = [],
  min,
  max,
  step,
  placeholder,
}) {
  const value = searchParams.get(urlKey) || "";
  return (
    <input
      type="number"
      className="ems-toolbar-select"
      aria-label={label}
      placeholder={placeholder || label}
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(event) => onChange(urlKey, event.target.value, extraClears)}
    />
  );
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const TOOLBAR_MONTH_OPTIONS = MONTH_NAMES.map((label, index) => ({
  value: String(index + 1),
  label,
}));

export function getToolbarYearOptions(span = 5) {
  const current = new Date().getFullYear();
  const years = [];
  for (let year = current - span; year <= current + 1; year += 1) {
    years.push({ value: String(year), label: String(year) });
  }
  return years;
}
